import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../auth";
import { NotificationBell } from "./NotificationBell";
import LiveHandoffAlerts from "./LiveHandoffAlerts";
import { api } from "../api";
import { toast } from "sonner";
import { AutoRefreshControl } from "../lib/autoRefresh";
import {
  LayoutDashboard, Package, Tags, ShoppingCart, Repeat, FileText,
  CreditCard, LifeBuoy, Percent, Users, ScrollText, LogOut, ShieldCheck, ShieldAlert, Wrench,
  Building2, Contact2, Target, Users2, CheckSquare, Stamp, Workflow, SlidersHorizontal, FileSignature, Inbox, CalendarClock, Store as StoreIcon, Paperclip, HardDrive, Flame, Mail,
  ChevronDown, ChevronRight, ChevronsLeft, ChevronsRight, Search, Plus, Activity, Radio, Zap, Copy, Clock, TrendingUp,
} from "lucide-react";

const SESSION_STARTED_KEY = "sh_session_started_at";

function formatElapsed(ms) {
  const totalMinutes = Math.floor(ms / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}min`;
  if (minutes > 0) return `${minutes} min`;
  return "agora mesmo";
}

function formatLoginTime(ts) {
  const d = new Date(ts);
  const now = new Date();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  if (d.toDateString() === now.toDateString()) return `Sessao iniciada hoje as ${hh}:${mm}`;
  const dd = String(d.getDate()).padStart(2, "0");
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  return `Sessao iniciada em ${dd}/${mo} as ${hh}:${mm}`;
}

function SessionBadge() {
  const [elapsed, setElapsed] = useState("agora mesmo");
  const [loginTimeLabel, setLoginTimeLabel] = useState("");
  useEffect(() => {
    const compute = () => {
      let started = parseInt(localStorage.getItem(SESSION_STARTED_KEY) || "0", 10);
      if (!started) {
        started = Date.now();
        localStorage.setItem(SESSION_STARTED_KEY, String(started));
      }
      setElapsed(formatElapsed(Date.now() - started));
      setLoginTimeLabel(formatLoginTime(started));
    };
    compute();
    const id = setInterval(compute, 30000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="hidden lg:flex items-center gap-1 text-[11px] text-slate-400 px-1.5" title={loginTimeLabel} data-testid="session-elapsed-badge">
      <Clock size={11} />
      <span>Sessao ha {elapsed}</span>
    </div>
  );
}

const NAV_GROUPS = [
  { section: null, items: [
    { to: "/app", label: "Dashboard", icon: LayoutDashboard, perm: "dashboard:read", end: true },
    { to: "/app/tarefas", label: "Tarefas", icon: CheckSquare, perm: "crm:read" },
  ]},
  { section: "CRM", items: [
    { to: "/app/crm/contas", label: "Contas", icon: Building2, perm: "crm:read" },
    { to: "/app/crm/contactos", label: "Contactos", icon: Contact2, perm: "crm:read" },
    { to: "/app/crm/oportunidades", label: "Oportunidades", icon: Target, perm: "crm:read" },
    { to: "/app/crm/oportunidades/kanban", label: "Funil de Vendas", icon: Target, perm: "crm:read" },
    { to: "/app/crm/mrr-dashboard", label: "MRR Historico", icon: TrendingUp, perm: "crm:read" },
    { to: "/app/crm/qualificacao", label: "Qualificacao de leads", icon: Flame, perm: "crm:read" },
    { to: "/app/crm/atividades", label: "Atividades", icon: Inbox, perm: "crm:read" },
    { to: "/app/inbox-canais", label: "Inbox multicanal", icon: Inbox, perm: "crm:read" },
    { to: "/app/handoffs", label: "Handoffs Chat", icon: LifeBuoy, perm: "crm:read" },
    { to: "/app/formularios", label: "Formularios", icon: FileText, perm: "crm:read" },
    { to: "/app/crm/consentimentos", label: "Consentimentos RGPD", icon: ShieldCheck, perm: "crm:read" },
    { to: "/app/crm/duplicados", label: "Rever duplicados", icon: Copy, perm: "crm:write" },
    { to: "/app/equipa-vendas", label: "Equipa e comissoes", icon: Users2, perm: "comissoes:read" },
    { to: "/app/agendamentos", label: "Agendamentos", icon: CalendarClock, perm: "crm:read" },
  ]},
  { section: "Vendas e CPQ", items: [
    { to: "/app/catalogo", label: "Catalogo", icon: Package, perm: "catalogo:read" },
    { to: "/app/loja", label: "Loja interna", icon: StoreIcon, perm: "encomendas:read" },
    { to: "/app/precos", label: "Precos e descontos", icon: Tags, perm: "precos:read" },
    { to: "/app/propostas", label: "Propostas e Contratos", icon: FileSignature, perm: "cpq:read" },
    { to: "/app/contratos/templates", label: "Templates contratos", icon: FileText, perm: "cpq:write" },
    { to: "/app/email-templates", label: "Templates email", icon: Mail, perm: "cpq:write" },
    { to: "/app/aprovacoes", label: "Aprovacoes", icon: Stamp, perm: "aprovacoes:read" },
    { to: "/app/workflows", label: "Workflows", icon: Workflow, perm: "workflows:read" },
    { to: "/app/encomendas", label: "Encomendas", icon: ShoppingCart, perm: "encomendas:read" },
  ]},
  { section: "Subscricoes e Faturacao", items: [
    { to: "/app/subscricoes", label: "Subscricoes", icon: Repeat, perm: "subscricoes:read" },
    { to: "/app/faturacao", label: "Faturacao", icon: FileText, perm: "faturacao:read" },
    { to: "/app/pagamentos", label: "Pagamentos", icon: CreditCard, perm: "pagamentos:read" },
  ]},
  { section: "Service Desk", items: [
    { to: "/app/suporte", label: "Suporte", icon: LifeBuoy, perm: "tickets:read" },
    { to: "/app/anexos-kb", label: "Central de Anexos", icon: Paperclip, perm: "tickets:read" },
  ]},
  { section: "Marketing", items: [
    { to: "/app/marketing", label: "Email Marketing", icon: Mail, perm: "marketing:read" },
  ]},
  { section: "Integracoes e Infra", items: [
    { to: "/app/painel-plataformas", label: "Painel de Plataformas", icon: Activity, perm: "subscricoes:read" },
    { to: "/app/webhook-events", label: "Webhooks", icon: Radio, perm: "auditoria:read" },
    { to: "/app/plataformas", label: "Plataformas", icon: Building2, perm: "*" },
    { to: "/app/cognietica", label: "CogniEtica", icon: ShieldAlert, perm: "tenant_db:read" },
    { to: "/app/privaxia", label: "Privaxia", icon: ShieldCheck, perm: "tenant_db:read" },
  ]},
  { section: "Cliente", items: [
    { to: "/app/portal-cliente", label: "Portal do cliente", icon: StoreIcon, perm: "cpq:read" },
  ]},
  { section: "Administracao", items: [
    { to: "/app/parceiros", label: "Parceiros e comissoes", icon: Percent, perm: "comissoes:read" },
    { to: "/app/comissoes", label: "Comissoes internas", icon: Percent, perm: "comissoes:read" },
    { to: "/app/utilizadores", label: "Utilizadores", icon: Users, perm: "usuarios:read" },
    { to: "/app/auditoria", label: "Auditoria", icon: ScrollText, perm: "auditoria:read" },
    { to: "/app/manutencao", label: "Manutencao", icon: Wrench, perm: "manutencao:read" },
    { to: "/app/dr", label: "Disaster Recovery", icon: HardDrive, perm: "*" },
    { to: "/app/seguranca", label: "Seguranca", icon: ShieldCheck, perm: "*" },
    { to: "/app/definicoes", label: "Definicoes", icon: SlidersHorizontal, perm: "definicoes:write" },
  ]},
];

const LS_COLLAPSED = "cu.sidebar.collapsed";
const LS_SECTIONS = "cu.sidebar.sections";

function initials(nome = "") {
  return nome.trim().split(/\s+/).slice(0, 2).map((p) => p[0] || "").join("").toUpperCase() || "?";
}

export default function Layout({ children }) {
  const { user, logout, can } = useAuth();
  const nav = useNavigate();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(LS_COLLAPSED) === "1");
  const [openSections, setOpenSections] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_SECTIONS) || "{}"); } catch { return {}; }
  });
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchGroups, setSearchGroups] = useState({});
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchPlatform, setSearchPlatform] = useState(() => localStorage.getItem("sh_search_platform") || "");
  const [platformOptions, setPlatformOptions] = useState([]);
  const [searchHighlight, setSearchHighlight] = useState(0);
  // Paths de conectores API-based nao configurados - escondemos do menu ate ficarem ativos
  const [hiddenConnectorPaths, setHiddenConnectorPaths] = useState([]);
  // Conectores dinamicos configurados (adicionamos ao menu em runtime)
  const [dynamicConnectors, setDynamicConnectors] = useState([]);
  const searchRef = useRef(null);
  const avatarRef = useRef(null);

  // Carrega estado dos conectores API-based para esconder items do menu quando nao configurados
  // e para adicionar dinamicos configurados
  useEffect(() => {
    let alive = true;
    const load = () => api.get("/connectors/status").then(({ data }) => {
      if (!alive) return;
      const hidden = (data || []).filter((c) => !c.configured).map((c) => c.ui_path);
      setHiddenConnectorPaths(hidden);
      const dyn = (data || []).filter((c) => c.kind === "dynamic" && c.configured);
      setDynamicConnectors(dyn);
    }).catch(() => {});
    load();
    // refresca a cada minuto para reagir a novas plataformas configuradas
    const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const groups = useMemo(() => {
    const withDynamic = NAV_GROUPS.map((g) => {
      if (g.section === "Integracoes e Infra") {
        const extras = dynamicConnectors.map((c) => ({
          to: c.ui_path,
          label: c.platform_name,
          icon: Zap,
          perm: "tenant_db:read",
        }));
        return { ...g, items: [...g.items, ...extras] };
      }
      return g;
    });
    return withDynamic
      .map((g) => ({ ...g, items: g.items.filter((n) => can(n.perm) && !hiddenConnectorPaths.includes(n.to)) }))
      .filter((g) => g.items.length > 0);
  }, [can, hiddenConnectorPaths, dynamicConnectors]);

  useEffect(() => { localStorage.setItem(LS_COLLAPSED, collapsed ? "1" : "0"); }, [collapsed]);
  useEffect(() => { localStorage.setItem(LS_SECTIONS, JSON.stringify(openSections)); }, [openSections]);

  const isSectionOpen = (label) => openSections[label] !== false; // default open

  const toggleSection = (label) => setOpenSections((s) => ({ ...s, [label]: s[label] === false ? true : false }));

  const doLogout = async () => { await logout(); nav("/login"); };

  // "/" ativa search global
  useEffect(() => {
    const handler = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if ((e.key === "/" || (e.key === "k" && (e.ctrlKey || e.metaKey))) && tag !== "input" && tag !== "textarea" && !e.target?.isContentEditable) {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key === "Escape") { setSearchOpen(false); setAvatarOpen(false); searchRef.current?.blur(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // fechar dropdowns ao clicar fora
  useEffect(() => {
    const onClick = (e) => {
      if (avatarRef.current && !avatarRef.current.contains(e.target)) setAvatarOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // pesquisa global (debounce) - usa endpoint unificado + filtro por plataforma
  useEffect(() => {
    if (!searchOpen) return;
    const t = setTimeout(async () => {
      const q = searchQ.trim();
      if (!q) { setSearchGroups({}); return; }
      setSearchLoading(true);
      try {
        const params = { q, limit: 6 };
        if (searchPlatform) params.plataforma = searchPlatform;
        const { data } = await api.get(`/global-search`, { params });
        setSearchGroups(data || {});
      } catch { setSearchGroups({}); }
      finally { setSearchLoading(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [searchQ, searchOpen, searchPlatform]);

  // carrega plataformas para o dropdown de filtro
  useEffect(() => {
    api.get("/platforms/lookup").then(({ data }) => {
      setPlatformOptions((data || []).map((p) => ({ value: p.nome, label: p.nome })));
    }).catch(() => setPlatformOptions([]));
  }, []);

  useEffect(() => { localStorage.setItem("sh_search_platform", searchPlatform || ""); }, [searchPlatform]);

  const chooseSearch = (to) => { setSearchOpen(false); setSearchQ(""); nav(to); };

  // constroi a lista plana de resultados (ordem visual) para keyboard nav
  const searchGroupsDef = [
    { key: "leads", label: "Leads", to: (r) => `/app/crm/contas/${r.id}` },
    { key: "contas", label: "Contas", to: (r) => `/app/crm/contas/${r.id}` },
    { key: "contactos", label: "Contactos", to: (r) => r.account_id ? `/app/crm/contas/${r.account_id}` : `/app/crm/contactos` },
    { key: "subscricoes", label: "Subscricoes", to: () => `/app/subscricoes` },
    { key: "tickets", label: "Tickets", to: () => `/app/suporte` },
  ];
  const flatResults = searchGroupsDef.flatMap((g) =>
    (searchGroups[g.key] || []).map((r) => ({ ...r, __groupKey: g.key, __to: g.to(r) }))
  );

  useEffect(() => { setSearchHighlight(0); }, [searchQ, searchPlatform]);

  const onSearchKeyDown = (e) => {
    if (!searchOpen) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSearchHighlight((h) => Math.min(h + 1, Math.max(flatResults.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSearchHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const target = flatResults[searchHighlight];
      if (target) {
        chooseSearch(target.__to);
      } else if (searchQ.trim()) {
        const qs = new URLSearchParams({ q: searchQ.trim() });
        if (searchPlatform) qs.set("plataforma", searchPlatform);
        chooseSearch(`/app/pesquisa?${qs.toString()}`);
      }
    }
  };

  const searchTotalRows = flatResults.length;

  // Badge de conversas WhatsApp nao lidas no menu
  const [waUnreadCount, setWaUnreadCount] = useState(0);
  useEffect(() => {
    const fetchUnread = () => api.get("/channels/letscall/whatsapp/unread-count")
      .then(({ data }) => setWaUnreadCount(data.count || 0)).catch(() => {});
    fetchUnread();
    const t = setInterval(fetchUnread, 30000);
    return () => clearInterval(t);
  }, []);

  // Badge de chamadas perdidas hoje no Dashboard
  const [missedCallsCount, setMissedCallsCount] = useState(0);
  useEffect(() => {
    const fetchMissed = () => api.get("/channels/letscall/missed-calls-count")
      .then(({ data }) => setMissedCallsCount(data.count || 0)).catch(() => {});
    fetchMissed();
    const t = setInterval(fetchMissed, 60000);
    return () => clearInterval(t);
  }, []);

  // SSE - notificacoes WhatsApp em tempo real
  useEffect(() => {
    const token = localStorage.getItem("cognihub_token");
    if (!token) return;
    const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const url = `${apiBase}/api/channels/events/stream?token=${encodeURIComponent(token)}`;
    let es = null;
    let reconnectTimer = null;

    const connect = () => {
      es = new EventSource(url);
      es.onmessage = (e) => {
        try {
          const evt = JSON.parse(e.data);
          if (evt.type === "whatsapp.message") {
            const { contact_name, phone, body } = evt.payload || {};
            toast(`Novo WhatsApp de ${contact_name || phone || "contacto"}`, {
              description: body ? body.slice(0, 80) : "",
              duration: 6000,
            });
            setWaUnreadCount((n) => n + 1);
            window.dispatchEvent(new CustomEvent("wa:new-message", { detail: evt.payload }));
          } else if (evt.type === "sms.message") {
            const { contact_name, phone, body } = evt.payload || {};
            toast(`Novo SMS de ${contact_name || phone || "contacto"}`, {
              description: body ? body.slice(0, 80) : "",
              duration: 6000,
            });
            window.dispatchEvent(new CustomEvent("sms:new-message", { detail: evt.payload }));
          } else if (evt.type === "call.missed") {
            const { contact_name, phone, resultado } = evt.payload || {};
            const resultadoLabel = {
              "nao_atendeu": "nao atendeu",
              "ocupado": "linha ocupada",
              "caixa_voz": "caixa de voz",
            }[resultado] || "perdida";
            toast.warning(`Chamada perdida - ${contact_name || phone || "desconhecido"}`, {
              description: `Resultado: ${resultadoLabel}. Verificar Dashboard.`,
              duration: 8000,
            });
            setMissedCallsCount((n) => n + 1);
            window.dispatchEvent(new CustomEvent("call:missed", { detail: evt.payload }));
          }
        } catch { /* noop */ }
      };
      es.onerror = () => {
        es?.close();
        reconnectTimer = setTimeout(connect, 10000);
      };
    };

    connect();
    return () => { es?.close(); clearTimeout(reconnectTimer); };
  }, []); // eslint-disable-line

  const linkBase = "group flex items-center gap-2.5 rounded-md text-[13px] transition-colors";
  const linkClass = ({ isActive }) => `${linkBase} ${collapsed ? "justify-center px-2 py-2" : "px-2.5 py-1.5"} ${
    isActive ? "bg-blue text-white shadow-sm" : "text-white/75 hover:bg-white/10 hover:text-white"
  }`;

  return (
    <div className="min-h-screen flex" style={{ background: "var(--content-bg)" }}>
      <LiveHandoffAlerts />
      <aside
        className={`bg-navy text-white flex flex-col fixed h-screen transition-[width] duration-200 z-30 ${collapsed ? "w-[68px]" : "w-64"}`}
        data-testid="sidebar"
      >
        {/* Brand */}
        <div className="h-14 flex items-center gap-2 px-3 border-b border-white/10 shrink-0">
          <div className="w-9 h-9 rounded-lg bg-blue/20 flex items-center justify-center shrink-0">
            <ShieldCheck className="text-light-blue" size={18} />
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <div className="font-bold text-[15px] tracking-tight leading-tight">SaaS-Hub</div>
              <div className="text-[9px] tracking-[0.2em] uppercase text-light-blue">Cogniseg</div>
            </div>
          )}
          <button
            data-testid="sidebar-collapse-toggle"
            onClick={() => setCollapsed((v) => !v)}
            title={collapsed ? "Expandir" : "Colapsar"}
            className="p-1.5 rounded hover:bg-white/10 text-white/70"
          >
            {collapsed ? <ChevronsRight size={15} /> : <ChevronsLeft size={15} />}
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto cu-scroll py-3 px-2 space-y-3">
          {groups.map((g, gi) => {
            const key = g.section || `g-${gi}`;
            const open = !g.section ? true : isSectionOpen(g.section);
            return (
              <div key={key} className="space-y-0.5">
                {g.section && !collapsed && (
                  <button
                    type="button"
                    onClick={() => toggleSection(g.section)}
                    data-testid={`nav-section-${g.section.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="w-full flex items-center gap-1 px-2 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/40 hover:text-white/70 transition-colors"
                  >
                    {open ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                    <span>{g.section}</span>
                  </button>
                )}
                {(open || collapsed) && g.items.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    title={collapsed ? n.label : undefined}
                    data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={linkClass}
                  >
                    <n.icon size={16} className="shrink-0" />
                    {!collapsed && <span className="truncate flex-1">{n.label}</span>}
                    {n.to === "/app/inbox-canais" && waUnreadCount > 0 && (
                      <span className="ml-auto shrink-0 text-[9px] font-bold bg-[#25d366] text-white rounded-full px-1.5 py-0.5 min-w-[18px] text-center"
                        data-testid="nav-wa-badge">{waUnreadCount}</span>
                    )}
                    {n.to === "/app" && n.end && missedCallsCount > 0 && (
                      <span className="ml-auto shrink-0 text-[9px] font-bold bg-red-500 text-white rounded-full px-1.5 py-0.5 min-w-[18px] text-center"
                        data-testid="nav-missed-calls-badge">{missedCallsCount}</span>
                    )}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>

        {/* User footer */}
        <div className="border-t border-white/10 p-2 shrink-0">
          {!collapsed ? (
            <div className="flex items-center gap-2 px-1.5 py-1">
              <div className="w-8 h-8 rounded-full bg-blue text-white text-xs font-semibold flex items-center justify-center shrink-0">{initials(user?.nome)}</div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold truncate">{user?.nome}</div>
                <div className="text-[10px] text-white/50 truncate">{(user?.roles || []).join(", ") || user?.email}</div>
              </div>
              <button onClick={doLogout} data-testid="logout-btn" title="Terminar sessao" className="p-1.5 rounded text-white/60 hover:text-white hover:bg-white/10">
                <LogOut size={15} />
              </button>
            </div>
          ) : (
            <button onClick={doLogout} data-testid="logout-btn" title="Terminar sessao" className="w-full flex justify-center p-2 rounded text-white/60 hover:text-white hover:bg-white/10">
              <LogOut size={15} />
            </button>
          )}
        </div>
      </aside>

      {/* Content shell */}
      <div className={`flex-1 flex flex-col min-w-0 transition-[margin] duration-200 ${collapsed ? "ml-[68px]" : "ml-64"}`}>
        {/* Topbar */}
        <header
          className="h-14 bg-white border-b sticky top-0 z-20 flex items-center gap-3 px-4"
          style={{ borderColor: "var(--topbar-border)" }}
          data-testid="topbar"
        >
          <div className="flex-1 max-w-2xl relative">
            <div className="relative flex items-stretch gap-1.5">
              <select
                value={searchPlatform}
                onChange={(e) => setSearchPlatform(e.target.value)}
                data-testid="global-search-platform"
                title="Filtrar por plataforma"
                className="text-[12px] font-medium bg-slate-100 border border-transparent rounded-md px-2 focus:bg-white focus:border-slate-300 focus:ring-2 focus:ring-blue/20 focus:outline-none transition"
              >
                <option value="">Todas</option>
                {platformOptions.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
              <div className="relative flex-1">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  ref={searchRef}
                  data-testid="global-search"
                  value={searchQ}
                  onChange={(e) => { setSearchQ(e.target.value); setSearchOpen(true); }}
                  onFocus={() => setSearchOpen(true)}
                  onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
                  onKeyDown={onSearchKeyDown}
                  placeholder="Pesquisar leads, contas, contactos, subscricoes, tickets..."
                  className="w-full pl-7 pr-16 py-1.5 text-[13px] bg-slate-100 rounded-md border border-transparent focus:bg-white focus:border-slate-300 focus:ring-2 focus:ring-blue/20 focus:outline-none transition"
                />
                <kbd className="absolute right-2 top-1/2 -translate-y-1/2 hidden sm:flex items-center gap-1 text-[10px] text-slate-500 bg-white border border-slate-200 rounded px-1.5 py-0.5">
                  Ctrl+K
                </kbd>
              </div>
            </div>
            {searchOpen && (searchQ.trim() || searchLoading) && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-96 overflow-y-auto z-40" data-testid="global-search-results">
                {searchLoading && <div className="px-3 py-2 text-xs text-slate-500">a pesquisar...</div>}
                {!searchLoading && searchTotalRows === 0 && searchQ.trim() && (
                  <div className="px-3 py-3 text-xs text-slate-500">
                    Sem resultados para "{searchQ}"
                    {searchPlatform && <> na plataforma <b>{searchPlatform}</b></>}.
                  </div>
                )}
                {!searchLoading && [
                  { key: "leads", label: "Leads", tone: "bg-purple-50 text-purple-700" },
                  { key: "contas", label: "Contas", tone: "bg-blue-50 text-blue-700" },
                  { key: "contactos", label: "Contactos", tone: "bg-teal-50 text-teal-700" },
                  { key: "subscricoes", label: "Subscricoes", tone: "bg-green-50 text-green-700" },
                  { key: "tickets", label: "Tickets", tone: "bg-amber-50 text-amber-700" },
                ].map(({ key, label, tone }) => {
                  const rows = searchGroups[key] || [];
                  if (!rows.length) return null;
                  return (
                    <div key={key} data-testid={`global-search-group-${key}`}>
                      <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
                      {rows.map((r) => {
                        const flatIdx = flatResults.findIndex((x) => x.__groupKey === key && x.id === r.id);
                        const active = flatIdx === searchHighlight;
                        return (
                        <button
                          key={`${key}-${r.id}`}
                          type="button"
                          onMouseEnter={() => setSearchHighlight(flatIdx)}
                          onMouseDown={(e) => { e.preventDefault(); chooseSearch(flatResults[flatIdx].__to); }}
                          data-testid={`global-search-item-${key}-${r.id}`}
                          className={`w-full text-left px-3 py-2 border-b border-slate-100 text-[13px] ${active ? "bg-blue/10" : "hover:bg-slate-50"}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded ${tone}`}>{label.slice(0, -1) || label}</span>
                            <span className="font-semibold text-slate-800 truncate flex-1">
                              {r.nome || r.assunto || r.organizacao_nome || r.plano_nome}
                            </span>
                            {r.plataforma && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{r.plataforma}</span>}
                            {r.estado && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{r.estado}</span>}
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                            {key === "leads" && r.origem && `origem ${r.origem}`}
                            {key === "leads" && r.nif && ` - NIF ${r.nif}`}
                            {key === "contas" && r.nif && `NIF ${r.nif}`}
                            {key === "contas" && r.tipo && ` - ${r.tipo}`}
                            {key === "contactos" && (r.email || r.organizacao_nome)}
                            {key === "subscricoes" && r.organizacao_nome && `${r.organizacao_nome}${r.plano_nome ? " - " + r.plano_nome : ""}${r.mrr ? " - " + r.mrr + " EUR/mes" : ""}`}
                            {key === "tickets" && `${r.numero || ""}${r.organizacao_nome ? " - " + r.organizacao_nome : ""}`}
                          </div>
                        </button>
                        );
                      })}
                    </div>
                  );
                })}
                {!searchLoading && searchQ.trim() && (
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      const qs = new URLSearchParams({ q: searchQ.trim() });
                      if (searchPlatform) qs.set("plataforma", searchPlatform);
                      chooseSearch(`/app/pesquisa?${qs.toString()}`);
                    }}
                    data-testid="global-search-see-all"
                    className="w-full text-left px-3 py-2 text-[12px] font-semibold text-blue bg-slate-50 hover:bg-blue/10 border-t border-slate-200"
                  >
                    Ver todos os resultados para "{searchQ}"{searchPlatform ? ` em ${searchPlatform}` : ""} -&gt;
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <SessionBadge />
            <button
              data-testid="topbar-quick-create"
              title="Criar rapido (em breve)"
              className="hidden sm:inline-flex items-center gap-1 bg-blue text-white text-[13px] font-medium px-2.5 py-1.5 rounded-md hover:bg-[var(--blue-hover)] transition-colors"
            >
              <Plus size={14} /> Criar
            </button>
            <NotificationBell />
            <AutoRefreshControl />
            <div className="relative" ref={avatarRef}>
              <button
                data-testid="topbar-avatar"
                onClick={() => setAvatarOpen((v) => !v)}
                className="w-8 h-8 rounded-full bg-navy text-white text-xs font-semibold flex items-center justify-center hover:opacity-90"
              >
                {initials(user?.nome)}
              </button>
              {avatarOpen && (
                <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-slate-200 rounded-lg shadow-lg z-40" data-testid="topbar-avatar-menu">
                  <div className="px-3 py-2 border-b border-slate-100">
                    <div className="text-[13px] font-semibold text-navy truncate">{user?.nome}</div>
                    <div className="text-[11px] text-slate-500 truncate">{user?.email}</div>
                  </div>
                  <button onClick={() => { setAvatarOpen(false); nav("/app/perfil"); }} data-testid="topbar-profile" className="w-full text-left px-3 py-2 text-[13px] hover:bg-slate-50 flex items-center gap-2 text-slate-700">
                    <ShieldCheck size={14} /> Perfil e MFA
                  </button>
                  <button onClick={doLogout} data-testid="topbar-logout" className="w-full text-left px-3 py-2 text-[13px] hover:bg-slate-50 flex items-center gap-2 text-slate-700">
                    <LogOut size={14} /> Terminar sessao
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 px-6 py-5 fade-in min-w-0">
          {children}
        </main>
      </div>
    </div>
  );
}
